import Tippy from "@tippyjs/react";
import { addDays, format, startOfDay } from "date-fns";
import React from "react";
import { ShiftObj } from "store/reducers";
import { useFetchShiftActivityInsightsQuery } from "store/reducers/attendance";

import { IconButton } from "../components/iconButton";
import { Duration } from "../timesheet_viewer/table_components/duration";

const activityTypes: Record<string, { label: string; help: string }> = {
  booking: { label: "Bookings created", help: "Patient bookings you created, excluding cancelled ones." },
  collating: { label: "Collating events", help: "Reports and documents you sent out, such as to referrers." },
  radphone_handled: { label: "Calls handled", help: "RadPhone calls you resolved." },
  smart_suggestions_used: {
    label: "Smart suggestions opened",
    help: "Whether you opened AI suggestions in the report editor.",
  },
  "added choice": { label: "Statements added", help: "Statements you added to report templates." },
  "modified choice": { label: "Statements modified", help: "Statements you edited in report templates." },
  "deleted choice": { label: "Statements removed", help: "Statements you removed from report templates." },
  registration: { label: "Registrations", help: "Reports, patients, referrers or practices you registered." },
  update_data: { label: "Data updates", help: "Changes you made to patient, referrer or practice records." },
  categorisation: { label: "Categorizations", help: "Sentences you categorized." },
  "signing-reports": { label: "Report sign-offs", help: "Reports you signed." },
  "write-note": { label: "Notes written", help: "Notes you added to patients, referrers or practices." },
};

type Props = {
  shiftObj: ShiftObj;
};

function rrRadresUrl(shiftObj: ShiftObj) {
  if (!shiftObj.start || !shiftObj.end) return;

  const params = new URLSearchParams({
    start_date: format(new Date(shiftObj.start), "yyyy-MM-dd"),
    end_date: format(addDays(startOfDay(new Date(shiftObj.end)), 1), "yyyy-MM-dd"),
  });
  return `https://radreport.adelaidemri.com/radres-activity?${params}`;
}

// Displaying Yes or No instead of the number of times Smart Suggestions were opened.
function formatEventCount(type: string, count: number) {
  if (type === "smart_suggestions_used") return count > 0 ? "Yes" : "No";
  return count;
}

export function ShiftActivityInsights({ shiftObj }: Props) {
  const { data, isLoading, isError } = useFetchShiftActivityInsightsQuery(shiftObj.worked_shift_id);
  const radresUrl = rrRadresUrl(shiftObj);
  const eventCounts = Object.entries(data?.event_counts ?? {}).filter(
    ([type, count]) => count > 0 || type === "smart_suggestions_used",
  );

  return (
    <section className="bg-primary-lightest text-primary p-4 rounded-sm mb-4">
      <h2 className="text-lg font-semibold mb-2">Here&apos;s your RadReport activity for this shift</h2>
      {isLoading && <p>Loading activity insights…</p>}
      {isError && <p>Activity insights are unavailable. Your clock-off has been saved.</p>}
      {data?.total_event_count === 0 && <p>No RadReport activity was recorded during this shift.</p>}
      {data && data.total_event_count > 0 && (
        <ul className="list-disc pl-5">
          {eventCounts.map(([type, count]) => {
            const activity = activityTypes[type];
            return (
              <li key={type}>
                {activity?.label ?? type.replace(/[_-]/g, " ")}: {formatEventCount(type, count)}
                {activity && (
                  <Tippy content={activity.help}>
                    <IconButton className="ml-1" icon="question-circle" />
                  </Tippy>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {data && data.total_event_count > 0 && (
        <p className="mt-3">
          Your longest period without recorded activity was{" "}
          <strong>
            <Duration seconds={data.longest_inactivity.duration_seconds} showZeroHours={false} />
          </strong>
          , from <strong>{format(new Date(data.longest_inactivity.start), "h:mm a")}</strong> to{" "}
          <strong>{format(new Date(data.longest_inactivity.end), "h:mm a")}</strong>.
        </p>
      )}
      {data && data.total_event_count > 0 && (
        <p className="text-sm mt-1">This may include breaks and work completed outside RadReport.</p>
      )}
      {radresUrl && (
        <a className="inline-block mt-3 underline" href={radresUrl} target="_blank" rel="noreferrer">
          View activity graphs in RadReport
        </a>
      )}
    </section>
  );
}
